import { createApiClient } from "#/shared/api/client";
import { abortableSleep, jitteredBackoff } from "#/shared/api/retry";

// The doctor-finder omnisearch data is assembled from three endpoints fetched in
// parallel:
//
//   1. autocomplete_data   — the skill taxonomy (specialties, conditions, …),
//                            the `omnisearch_result_types` ordering, and the
//                            physician/location *ids* to surface.
//   2. autocomplete_lookup — an id-keyed hydration table (name, icon, subtext)
//                            for the physicians/locations referenced by (1).
//   3. physicians/filters  — the visit-type / provider-type facets.
//
// The `.api_provider_search.json` segment selects the provider-search jbuilder
// variant (a format, not the `?api=` query param). The data endpoint is built
// asynchronously and answers `204 No Content` while its cache warms — callers
// must retry (see `getWithRetry`).
const DATA_PATH = "/physicians/doctor_finder_autocomplete_data.api_provider_search.json";
const LOOKUP_PATH = "/physicians/doctor_finder_autocomplete_lookup.api_provider_search.json";
const FILTERS_PATH = "/physicians/filters.json";

// The data endpoint answers `204 No Content` while its cache warms. Poll it,
// bounded: the cache warms in seconds, so cap the attempts and throw past that
// rather than letting a wedged build dogpile every embed on every tab forever.
// The store's `doLoad` catch surfaces the throw on `error`. Each wait is a
// jittered exponential backoff capped at RETRY_MAX_DELAY_MS, so a slow warm
// keeps polling without hammering a stuck build in lockstep across tabs.
const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 4000;
const MAX_RETRIES = 30;

// `ApiClient` returns `null` for a 204, which for the data endpoint means "still
// building, try again". Retry until a document arrives. Other endpoints never
// 204, so they use a plain `get`. `ApiClient` serializes the nested `search`
// scope and array params Rails-style, so `params` is passed as a plain object.
async function getWithRetry(api, params, { signal } = {}) {
  let data = await api.request("get", "", { params, signal });

  for (let attempt = 0; data == null && attempt < MAX_RETRIES; attempt += 1) {
    const backoff = jitteredBackoff(attempt, {
      baseMs: RETRY_BASE_DELAY_MS,
      maxMs: RETRY_MAX_DELAY_MS,
      jitterFloor: 0.5,
    });
    await abortableSleep(backoff, signal);
    data = await api.request("get", "", { params, signal });
  }

  if (data == null) {
    throw new Error(`omnisearch data endpoint still building after ${MAX_RETRIES} retries`);
  }

  return data;
}

/**
 * @param {Object} options
 * @param {Object} [options.dataParams] - params forwarded to the data endpoint
 *   (specialty_type, specialty_hierarchy, common_specialties,
 *   common_specialty_synonyms, search), exactly as the legacy fetcher sent them.
 * @param {String} [options.filtersApi] - the `?api=` value for the filters facet
 *   endpoint (the visit/provider-type sections use the v2.1 contract).
 */
export const createOmnisearchApi = ({ dataParams = {}, filtersApi = "provider_search_v2_1" } = {}) => {
  // These endpoints return plain JSON documents (section arrays + ordering), not
  // the `{ meta, data }` envelope ApiClient models — `parse: false` hands them back raw.
  const dataApi = createApiClient({ baseURL: DATA_PATH, parse: false });
  const lookupApi = createApiClient({ baseURL: LOOKUP_PATH, parse: false });
  const filtersApi_ = createApiClient({ baseURL: FILTERS_PATH, api: filtersApi, parse: false });

  return {
    data: ({ signal } = {}) => getWithRetry(dataApi, dataParams, { signal }),
    lookup: ({ signal } = {}) => lookupApi.request("get", "", { signal }),
    filters: ({ signal } = {}) =>
      filtersApi_.request("get", "", { params: { sections: "visit_types,provider_types" }, signal }),
  };
};
